export type LiveQuestTrack = 'basic' | 'plus';

const configuredSeasonId = (process.env.REACT_APP_LIVE_QUEST_SEASON_ID || 'season-1').trim().toLowerCase();
export const LIVE_QUEST_SEASON_ID = /^season-[a-z0-9][a-z0-9-]*$/.test(configuredSeasonId)
  ? configuredSeasonId
  : 'season-1';
export const LIVE_QUEST_SEASON_NAME = (process.env.REACT_APP_LIVE_QUEST_SEASON_NAME || 'Season 1').trim();
export const LIVE_QUEST_PRODUCT_KEY_PREFIX = `LIVE_QUEST_${LIVE_QUEST_SEASON_ID.replace(/-/g, '_').toUpperCase()}_`;

export type LiveQuestRewardSlot = {
  id: string;
  productKey: string;
  level: number;
  track: LiveQuestTrack;
  title: string;
  subtitle: string;
  rewardType: string;
  amount: number | null;
};

export type LiveQuestLeaderboardPrizeAsset = {
  id: string;
  rank: 1 | 2 | 3;
  title: string;
  tokenLabel: string;
};

export type LiveQuestUiAsset = {
  id: string;
  title: string;
  group: 'Permanent ranks' | 'XP Boost' | 'Onboarding and guides' | 'Celebrations' | 'Shared UI art';
  description: string;
  assetKind?: 'hero' | 'guide_video' | 'onboarding_icon';
};

/**
 * Season 1 rewards, level by level: [level, Free track, Plus track] (product list of 2026-10-12).
 * Mirror of tezda-ws `services/liveQuest/catalog.ts`; change both together.
 */
export const SEASON_1_REWARD_TABLE: [number, string, string][] = [
  [1, '100 PCoins', '+200 PCoins'],
  [2, 'Sticker Pack #1', '+50 PCoins'],
  [3, '100 PCoins', '+100 PCoins'],
  [4, '100 PCoins', '+100 PCoins'],
  [5, 'Chat Theme #1', 'Premium Chat Theme #1'],
  [6, '100 PCoins', '+100 PCoins'],
  [7, '+20 Tokens', '+100 PCoins'],
  [8, '100 PCoins', '+150 PCoins'],
  [9, '100 PCoins', '+100 PCoins'],
  [10, 'Profile Frame #1', 'Premium Profile Frame #1'],
  [11, '100 PCoins', '+150 PCoins'],
  [12, 'Sticker Pack #2', '+25 Tokens'],
  [13, '100 PCoins', '+150 PCoins'],
  [14, '100 PCoins', '+150 PCoins'],
  [15, 'Profile Background #1', 'Premium Background #1'],
  [16, '100 PCoins', '+150 PCoins'],
  [17, '+30 Tokens', '+50 Tokens'],
  [18, '100 PCoins', '+200 PCoins'],
  [19, '100 PCoins', '+150 PCoins'],
  [20, 'PingTop Plus (2 Days)', '+200 PCoins'],
  [21, 'Sticker Pack #3', '+150 PCoins'],
  [22, '100 PCoins', 'Premium Sticker Pack #1'],
  [23, '100 PCoins', '+200 PCoins'],
  [24, '100 PCoins', '+150 PCoins'],
  [25, 'Entry Animation #1', 'Premium Entry Animation #1'],
  [26, '100 PCoins', '+200 PCoins'],
  [27, '+50 Tokens', '+75 Tokens'],
  [28, '100 PCoins', '+200 PCoins'],
  [29, '100 PCoins', '+200 PCoins'],
  [30, 'Chat Theme #2', 'Premium Chat Theme #2'],
  [31, '100 PCoins', '+200 PCoins'],
  [32, 'Profile Background #2', '+200 PCoins'],
  [33, '100 PCoins', '+100 Tokens'],
  [34, '100 PCoins', '+200 PCoins'],
  [35, 'Profile Frame #2', 'Premium Profile Frame #2'],
  [36, '100 PCoins', '+250 PCoins'],
  [37, '100 PCoins', 'Premium Sticker Pack #2'],
  [38, '100 PCoins', '+250 PCoins'],
  [39, '100 PCoins', '+200 PCoins'],
  [40, 'Entry Animation #2', 'Premium Entry Animation #2'],
  [41, '100 PCoins', '+250 PCoins'],
  [42, '100 PCoins', '+200 PCoins'],
  [43, '100 PCoins', '+50 Tokens'],
  [44, '100 PCoins', '+250 PCoins'],
  [45, '500 PCoins', '+500 PCoins'],
  [46, '100 PCoins', '+250 PCoins'],
  [47, '100 PCoins', '+200 PCoins'],
  [48, '100 PCoins', '+100 Tokens'],
  [49, '300 PCoins', 'PingTop Plus (+7 Days)'],
  [50, `${LIVE_QUEST_SEASON_NAME} Badge + Elite Profile Frame`, 'Elite Animated Frame'],
];

const SUBTITLES: Record<string, string> = {
  pcoins: 'In-app currency',
  tokens: 'Tokens',
  chatTheme: 'Chat Theme',
  profileBackground: 'Profile Background',
  stickerPack: 'Sticker Pack',
  profileFrame: 'Profile Frame',
  plusDuration: 'PingTop Plus',
  entryAnimation: 'Entry Animation',
  seasonBadge: 'Grand Finale Milestone',
};

export const rewardFromTitle = (title: string): { rewardType: string; amount: number | null } => {
  const amount = title.match(/(\d+)/)?.[1];
  if (/PCoins$/.test(title)) return { rewardType: 'pcoins', amount: Number(amount) };
  if (/Tokens$/.test(title)) return { rewardType: 'tokens', amount: Number(amount) };
  if (/PingTop Plus/.test(title)) return { rewardType: 'plusDuration', amount: Number(amount) };
  if (/Sticker Pack/.test(title)) return { rewardType: 'stickerPack', amount: null };
  if (/Chat Theme/.test(title)) return { rewardType: 'chatTheme', amount: null };
  if (/Background/.test(title)) return { rewardType: 'profileBackground', amount: null };
  if (/Entry Animation/.test(title)) return { rewardType: 'entryAnimation', amount: null };
  if (/Badge/.test(title)) return { rewardType: 'seasonBadge', amount: null };
  if (/Profile Frame|Animated Frame/.test(title)) return { rewardType: 'profileFrame', amount: null };
  throw new Error(`Unknown Live Quest reward: ${title}`);
};

const buildTrack = (track: LiveQuestTrack): LiveQuestRewardSlot[] =>
  SEASON_1_REWARD_TABLE.flatMap(([level, free, premium]) => {
    const title = track === 'plus' ? premium : free;
    const { rewardType, amount } = rewardFromTitle(title);
    const id = `${track === 'basic' ? 'free' : 'plus'}_${level}`;
    if (id === 'free_50') {
      return [
        {
          id: 'free_50_badge',
          productKey: liveQuestProductKey('free_50', 'BADGE'),
          level,
          track,
          title: `${LIVE_QUEST_SEASON_NAME} Badge`,
          subtitle: 'Season Badge',
          rewardType: 'seasonBadge',
          amount: null,
        },
        {
          id: 'free_50_frame',
          productKey: liveQuestProductKey('free_50'),
          level,
          track,
          title: 'Elite Profile Frame',
          subtitle: 'Profile Frame',
          rewardType: 'profileFrame',
          amount: null,
        },
      ];
    }
    return [{
      id,
      productKey: liveQuestProductKey(id),
      level,
      track,
      title,
      subtitle: SUBTITLES[rewardType],
      rewardType,
      amount,
    }];
  });

export const LIVE_QUEST_REWARD_SLOTS: LiveQuestRewardSlot[] = [...buildTrack('basic'), ...buildTrack('plus')];

/** Display artwork for the three Season 1 leaderboard prize cards. */
export const LIVE_QUEST_LEADERBOARD_PRIZE_ASSETS: LiveQuestLeaderboardPrizeAsset[] = [
  { id: 'leaderboard_champion', rank: 1, title: 'Champion Frame', tokenLabel: '1,000 Tokens' },
  { id: 'leaderboard_runner_up', rank: 2, title: 'Runner-Up Frame', tokenLabel: '700 Tokens' },
  { id: 'leaderboard_top_3', rank: 3, title: 'Top 3 Frame', tokenLabel: '500 Tokens' },
];

const RANKS = [
  'Newcomer', 'Explorer', 'Rising Star', 'Challenger', 'Achiever', 'Elite', 'Master', 'Grandmaster',
  'Legend', 'Mythic', 'Champion', 'Titan', 'Immortal', 'Ascendant', 'Celestial', 'Eternal', 'Infinite Legend',
];

const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

/** Dashboard-managed artwork used by the redesigned flow outside the 100 seasonal reward stages. */
export const LIVE_QUEST_UI_ASSETS: LiveQuestUiAsset[] = [
  ...RANKS.map((title) => ({
    id: `rank_${slug(title)}`,
    title: `${title} rank badge`,
    group: 'Permanent ranks' as const,
    description: 'Permanent PingTop Level rank badge. The Level number remains live UI text.',
  })),
  { id: 'xp_boost_hero', title: 'XP Boost illustration', group: 'XP Boost', description: 'Main XP Boost screen artwork.' },
  ...[1000, 2500, 5000, 10000].map((xp) => ({
    id: `xp_pack_${xp}`,
    title: `${xp.toLocaleString()} XP pack`,
    group: 'XP Boost' as const,
    description: 'Pack artwork only; XP and Token price remain server controlled.',
  })),
  { id: 'onboarding_hero', title: 'Live Quest onboarding', group: 'Onboarding and guides', description: 'Onboarding illustration.' },
  { id: 'onboarding_token_icon', title: 'Onboarding Tokens icon', group: 'Onboarding and guides', description: 'Tokens tile on the first Live Quest onboarding page.', assetKind: 'onboarding_icon' },
  { id: 'onboarding_profile_frame_icon', title: 'Onboarding profile frame icon', group: 'Onboarding and guides', description: 'Profile Frames tile on the first Live Quest onboarding page. Use an actual Season 1 reward image.', assetKind: 'onboarding_icon' },
  { id: 'onboarding_entry_animation_icon', title: 'Onboarding entry animation icon', group: 'Onboarding and guides', description: 'Entry Animation tile on the first Live Quest onboarding page. Use an actual Season 1 reward image.', assetKind: 'onboarding_icon' },
  { id: 'onboarding_sticker_pack_icon', title: 'Onboarding sticker pack icon', group: 'Onboarding and guides', description: 'Sticker Packs tile on the first Live Quest onboarding page. Use an actual Season 1 reward image.', assetKind: 'onboarding_icon' },
  ...[
    ['watch', 'Watch'], ['chat', 'Chat'], ['engage', 'Engage'], ['level_up', 'Level Up'],
    ['levels', 'Total levels'], ['tracks', 'Free & Plus'], ['exclusive', 'Exclusive'],
    ['bigger_rewards', 'Bigger Rewards'],
  ].map(([id, title]) => ({
    id: `onboarding_${id}_icon`, title: `${title} onboarding icon`,
    group: 'Onboarding and guides' as const,
    description: `Uploaded ${title} icon for the Live Quest onboarding pages.`,
    assetKind: 'onboarding_icon' as const,
  })),
  {
    id: 'onboarding_intro_video',
    title: 'Live Quest intro video',
    group: 'Onboarding and guides',
    description: 'Plays before the Season 1 onboarding pages; Skip opens the first page.',
    assetKind: 'guide_video',
  },
  { id: 'level_earning_guide', title: 'Level earning guide', group: 'Onboarding and guides', description: 'Permanent Level earning guide artwork.' },
  { id: 'rank_journey', title: 'Rank journey', group: 'Onboarding and guides', description: 'Rank journey artwork.' },
  ...[
    ['earn_xp', 'Earn XP'],
    ['rank_badges', 'Unlock Rank Badges'],
    ['show_badge', 'Show Your Badge'],
    ['inactivity', '180-Day Activity Rule'],
  ].map(([id, title]) => ({
    id: `level_join_${id}_icon`,
    title: `${title} join icon`,
    group: 'Onboarding and guides' as const,
    description: `Uploaded icon for the ${title} card on Join PingTop Level.`,
    assetKind: 'onboarding_icon' as const,
  })),
  {
    id: 'how_it_works_video',
    title: 'How It Works video',
    group: 'Onboarding and guides',
    description: 'Full-screen guide video opened from Live Quest Settings.',
    assetKind: 'guide_video',
  },
  { id: 'celebration_rank_up', title: 'New rank celebration', group: 'Celebrations', description: 'Permanent rank-up celebration artwork.' },
  { id: 'celebration_stage_up', title: 'Stage celebration', group: 'Celebrations', description: 'Seasonal stage-up celebration artwork.' },
  { id: 'celebration_daily_complete', title: 'Daily completion celebration', group: 'Celebrations', description: 'Final daily mission and bonus celebration artwork.' },
  { id: 'celebration_reward', title: 'Reward collected celebration', group: 'Celebrations', description: 'Reward collection popup artwork.' },
  { id: 'quest_icon', title: 'Live Quest icon', group: 'Shared UI art', description: 'Compact seasonal identity icon.' },
  { id: 'xp_icon', title: 'XP icon', group: 'Shared UI art', description: 'XP currency icon.' },
  { id: 'token_icon', title: 'Token icon', group: 'Shared UI art', description: 'Token currency icon used in XP purchase screens.' },
  { id: 'live_quest_wordmark', title: 'Live Quest wordmark', group: 'Shared UI art', description: 'Custom Live Quest wordmark when supplied as artwork.' },
  { id: 'hub_backdrop', title: 'Hub backdrop', group: 'Shared UI art', description: 'Authored hub backdrop.' },
  { id: 'level_detail_backdrop', title: 'Level detail backdrop', group: 'Shared UI art', description: 'Authored permanent Level detail backdrop.' },
];

export function liveQuestProductKey(rewardId: string, suffix?: string) {
  return `${LIVE_QUEST_PRODUCT_KEY_PREFIX}${rewardId.toUpperCase()}${suffix ? `_${suffix}` : ''}`;
}

/** Reward id of the Season 1 hero crest (the header artwork). Not a reward: it has no level or payout. */
export const LIVE_QUEST_HERO_ID = 'hero';
